import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../../models/Paginate/PagesPaginate";
import { SubscriptionsService } from "../../../services/Financial/Subscriptions/SubscriptionsServices";
import { SubscriptionsModel } from "../../../models/Financial/Subscriptions/SubscriptionsModel";

interface ContextProps {
  params: any;
  setParams: Dispatch<SetStateAction<any>>;
  subscriptions: SubscriptionsModel[];
  pages: PagesPaginateModel;
  setPages: Dispatch<SetStateAction<PagesPaginateModel>>;
  isLoading: boolean;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  handleList: () => void;
}

export const SubscriptionsContext = createContext<ContextProps>(
  {} as ContextProps,
);

interface SubscriptionsProviderProps {
  children: ReactNode;
}

export const SubscriptionsProvider = ({
  children,
}: SubscriptionsProviderProps) => {
  const [params, setParams] = useState<any>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [subscriptions, setSubscriptions] = useState<SubscriptionsModel[]>([]);

  const service = new SubscriptionsService();
  const location = useLocation();

  const handleList = useCallback(async () => {
    if (
      !params ||
      !location.pathname.includes("/financial/subscriptions")
    ) {
      return false;
    }

    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.list(params);
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setSubscriptions(_Response?.data?.items);
      setPages(_Response?.data?.pagination);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setError("Não foi possível carregar as inscrições do sistema.");
    }
  }, [params, location.pathname]);

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleList().then();
  }, [handleList]);

  return (
    <SubscriptionsContext.Provider
      value={{
        params,
        setParams,
        subscriptions,
        pages,
        setPages,
        isLoading,
        error,
        setError,
        handleList,
      }}
    >
      {children}
    </SubscriptionsContext.Provider>
  );
};

export const useSubscriptions = () => useContext(SubscriptionsContext);
